import Close20 from '@carbon/icons-react/es/close/20';
import Download20 from '@carbon/icons-react/es/download/20';
import RotateClockwise20 from '@carbon/icons-react/es/rotate--clockwise/20';
import RotateCounterclockwise20 from '@carbon/icons-react/es/rotate--counterclockwise/20';
import ZoomIn20 from '@carbon/icons-react/es/zoom--in/20';
import ZoomOut20 from '@carbon/icons-react/es/zoom--out/20';
import { ImageProps, Modal } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { RoutePaths } from 'constant';
import { useLocalStorageSession } from 'hooks/use-local-storage-session';
import { extname } from 'utils/files';
import { ActionsBar } from '../action-bar';
import {
  fileFormatSupported,
  localStorageWorkspacePath,
  nonImageFileToPreview,
} from './constants';

const StyledModal = styled(Modal)`
  height: 100%;
  width: 100%;
  top: 0;
  max-height: 100vh;

  .ant-modal-content {
    background-color: transparent;
    width: 100%;
    box-shadow: none;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-footer {
    display: none;
  }
`;

const StyledPreviewImage = styled.img`
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  object-fit: cover;
  transition: linear 0.5s;
  margin-top: var(--spacing-09);
`;

const StyledPreviewIframe = styled.iframe`
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  transition: linear 0.5s;
  margin-top: var(--spacing-09);
  height: calc(100vh - var(--spacing-09));
  width: 80%;
  border: none;
`;

const StyledActionBar = styled(ActionsBar)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  height: var(--spacing-09);
`;

const ActionBarButton = styled(Button).attrs(() => ({
  type: 'link',
}))`
  margin-right: var(--spacing-03);

  svg {
    fill: white;
  }
`;

const StartAnnotationButton = styled(Button)`
  color: var(--white-100);
  border-color: var(--white-100);
  background: none;
  padding: 0 var(--spacing-04);
  max-height: var(--spacing-07);
  margin-right: var(--spacing-05);

  &:hover {
    color: var(--white-100);
    background: var(--primary-color);
    border-color: var(--primary-color);
  }
`;

export type BasicImagePreviewProps = ImageProps & {
  fileName: string;
  data?: number;
  onDownload?(): void;
  preview: boolean;
  onClose?(): void;
  isBasicPreview?: boolean;
};

export const BasicImagePreview: FC<BasicImagePreviewProps> = ({
  data,
  fileName,
  onDownload,
  preview,
  onClose,
  isBasicPreview,
  ...props
}) => {
  const [rotate, setRotate] = useState(0);
  const [scale, setScale] = useState(1);
  const [fileExtension, setFileExtension] = useState('');
  const history = useHistory();
  const { pathname } = useLocation();
  const { setItem } = useLocalStorageSession(
    localStorageWorkspacePath,
    pathname,
  );

  useEffect(() => {
    const fileExtensionValue = extname(props.src || fileName);

    if (fileExtensionValue) {
      setFileExtension(extname(props.src || fileName).toLowerCase());
    }
  }, [fileName, props.src]);

  const openFileInWorkspace = useCallback(
    (e) => {
      e.stopPropagation();
      if (data) {
        setItem();
        history.push(
          RoutePaths.attachmentWorkspace.replace(
            ':attachmentId',
            data.toString(),
          ),
        );
      }
    },
    [data, setItem, history],
  );

  const isFileFormatNotSupportedForAnnotation =
    typeof fileExtension === 'string' &&
    !fileFormatSupported.includes(fileExtension);

  return (
    <>
      <StyledModal visible={preview} width="100%" centered closable={false}>
        <StyledActionBar>
          {!nonImageFileToPreview.includes(fileExtension) && (
            <>
              <ActionBarButton
                icon={<Download20 />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload?.();
                }}
              />
              <ActionBarButton
                icon={<RotateClockwise20 />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRotate((prevState) => prevState + 90);
                }}
              />
              <ActionBarButton
                icon={<RotateCounterclockwise20 />}
                onClick={(e) => {
                  e.stopPropagation();
                  setRotate((prevState) => prevState - 90);
                }}
              />
              <ActionBarButton
                icon={<ZoomIn20 />}
                onClick={(e) => {
                  e.stopPropagation();
                  setScale((prevState) => prevState + 1);
                }}
              />
              <ActionBarButton
                icon={<ZoomOut20 />}
                onClick={(e) => {
                  e.stopPropagation();
                  setScale((prevState) =>
                    prevState > 1 ? prevState - 1 : prevState,
                  );
                }}
              />
            </>
          )}
          {!isBasicPreview ? (
            <StartAnnotationButton
              onClick={openFileInWorkspace}
              disabled={isFileFormatNotSupportedForAnnotation}
            >
              <FormattedMessage id="image_preview.annotate" />
            </StartAnnotationButton>
          ) : null}
          <ActionBarButton
            icon={<Close20 />}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          />
        </StyledActionBar>
        <div>
          {nonImageFileToPreview.includes(fileExtension) ? (
            <StyledPreviewIframe src={props.src} />
          ) : (
            <StyledPreviewImage
              src={props.src}
              alt={props.alt}
              style={{
                transform: `translate(-50%) rotate(${rotate}deg)`,
                height: `calc(${scale}*80%)`,
                width: `calc(${scale}*80%)`,
              }}
            />
          )}
        </div>
      </StyledModal>
    </>
  );
};
